import {
  GetIndexHistories200Response,
  GetMarkets200Response,
  GetMarketsSummary200ResponseSummariesInner,
  GetStatistics200Response,
  InstrumentTypeResponse,
} from "../../codegen-api";
import { isInstrumentTypeEnabled } from "../../config/features";
import { optionsEnabledAssets } from "../../constants/assets";
import { IMarket } from "../../contexts/MarketContext";
import { instrumentLabel } from "../../utils/instrumentLabel";
import { apiNum } from "../../utils/math";
import { knownPrice } from "../../utils/unknownNumbers";
import { MarketSearchKeys, rankMarketRows } from "./marketSearch";
import { getConfigPricePrecision } from "./utils";
import {
  IOptionMarketData,
  IPerpetualMarketData,
} from "./MarketsTable";
import {
  ISectionData,
  MarketFiltersEnum,
} from "./CommandModal.types";
import {
  normalizeMarketCategory,
  perpCategoryFilters,
  sortWatchlist,
} from "./CommandModal.helpers";

export type SearchResult = GetMarketsSummary200ResponseSummariesInner & {
  display_name?: string;
  market_category?: string;
};

/**
 * A spot row's 24h change as a fraction, by the trading header's rule
 * (PerpsTradingHeader.helpers): measured against a REAL 24h-ago price, from
 * the current mark when there is one, else from the reported change. No
 * reference, no percentage -- `undefined` renders "-".
 */
export const spotDailyChange = (
  stats: GetStatistics200Response | undefined,
  fallbackMark?: string | number | null
): number | undefined => {
  const reference = knownPrice((stats as any)?.mark_price_24h_ago);
  if (reference === undefined) return undefined;
  const mark = knownPrice(stats?.mark_price) ?? knownPrice(fallbackMark);
  if (mark !== undefined) return (mark - reference) / reference;
  const change = apiNum((stats as any)?.mark_daily_change);
  return change !== undefined ? change / reference : undefined;
};

type Tagged<T> = { row: T; keys: MarketSearchKeys };

export const computeSectionsData = (params: {
  searchResults: SearchResult[];
  watchlist: IMarket[];
  market: IMarket;
  marketFilter: MarketFiltersEnum;
  assetIndexHistories: GetIndexHistories200Response | undefined;
  t: (key: string) => string;
  /** Scoped to the `tradingTypes` i18n block — names the per-vertical sections. */
  tTradingType: (key: string) => string;
  searchText: string;
  uniqueSpotAssets: string[];
  spotMarketsData: GetMarkets200Response[] | undefined;
  /**
   * `/statistics` per spot asset -- the same source the trading header reads
   * its 24h change and volume from. Absent while loading or when unknown.
   */
  spotStatistics?: Record<string, GetStatistics200Response | undefined>;
}): ISectionData[] => {
  const {
    searchResults,
    watchlist,
    market,
    marketFilter,
    assetIndexHistories,
    t,
    tTradingType,
    searchText,
    uniqueSpotAssets,
    spotMarketsData,
    spotStatistics,
  } = params;
    const perpsEnabled = isInstrumentTypeEnabled(InstrumentTypeResponse.Perpetual);
    const isSearching = !!searchText.trim();

    // Searching is ONE ranking across every vertical's rows (marketSearch);
    // without a query each section keeps its natural, watchlist-first order.
    // Rows carry their rank so a section can be ordered by it afterwards.
    const rankOf = new Map<unknown, number>();
    const finish = <T extends { isWatchlist: boolean }>(
      tagged: Tagged<T>[]
    ): T[] =>
      tagged
        .map((t) => t.row)
        .filter((row) =>
          marketFilter === MarketFiltersEnum.WATCHLIST ? row.isWatchlist : true
        )
        .filter((row) => !isSearching || rankOf.has(row))
        .sort((a, b) =>
          isSearching
            ? (rankOf.get(a) as number) - (rankOf.get(b) as number)
            : sortWatchlist(a as any, b as any)
        );

    const optionsTagged: Tagged<IOptionMarketData>[] = [...searchResults]
      .filter((d) => !!optionsEnabledAssets[d.asset])
      .map((d) => {
        const putOI = Number(d.option_info?.open_interest?.puts);
        const callOI = Number(d.option_info?.open_interest?.calls);
        const putCallRatio = putOI && callOI ? putOI / callOI : 0;

        // OI in USD needs a quoted index price. `/markets-summary` sends null
        // for an unknown one, and `Number(null) * total` reported $0 of open
        // interest (then `|| 0` below made every unknown a confident 0).
        const indexPrice = apiNum(d.index_price);
        const openInterestTotal = apiNum(d.option_info?.open_interest?.total);
        const openInterestUSD =
          indexPrice !== undefined && indexPrice > 0 && openInterestTotal !== undefined
            ? indexPrice * openInterestTotal
            : undefined;

        const row: IOptionMarketData = {
          id: `OPTION-${d.asset}`,
          asset: d.asset,
          putCallRatio,
          oi: openInterestUSD,
          dailyVolume: apiNum(d.option_info?.daily_volume),
          isWatchlist: watchlist.some(
            (w) =>
              w.asset === d.asset &&
              w.derivative === InstrumentTypeResponse.Option
          ),
          isCurrentMarket:
            market.asset === d.asset &&
            market.derivative === InstrumentTypeResponse.Option,
          pricePrecision: getConfigPricePrecision(
            d.asset,
            d.option_info?.amount_step,
            d.option_info?.price_step
          ),
          indexHistory: [],
        };
        return {
          row,
          keys: {
            label: d.asset,
            asset: d.asset,
            displayName: d.display_name,
            category: d.market_category,
          },
        };
      });

    const perpTagged: Tagged<IPerpetualMarketData>[] = [...searchResults]
      .filter((d) => !!d.perpetual_info)
      .map((d) => {
        // `apiNum` keeps a genuine "0" distinct from an absent field. Truthiness
        // cannot: several upstreams defaulted an unknown 24h change to the
        // STRING "0", which is truthy, so every unmeasured market rendered a
        // green, confident "0.00%" while the trading header for the same market
        // showed "-". Absent stays absent, and the cell renders "-".
        const change =
          apiNum(d.perpetual_info?.mark_daily_change) ?? apiNum(d.index_daily_change);
        const currentPrice =
          apiNum(d.perpetual_info?.mark_price) ?? apiNum(d.index_price);
        // The reference must be a REAL 24h price, the same rule the trading
        // header uses -- otherwise the two disagree about the same market.
        // Deriving it as `current - change` looks equivalent but is not: when an
        // upstream defaults an unknown change to "0" (as the deployed backend
        // still does), the derivation hands back `current`, and 0/current is a
        // confident "0.00%" for a market nobody has priced. No reference, no
        // percentage.
        // Cast: /markets-summary returns mark_price_24h_ago on perpetual_info
        // (verified live), but the OpenAPI schema omits it and codegen-api is
        // generated, not hand-edited -- same reason market_category is cast above.
        const price24hAgo = apiNum(
          (d.perpetual_info as any)?.mark_price_24h_ago
        );
        const dailyChange =
          change !== undefined && price24hAgo !== undefined && price24hAgo > 0
            ? change / price24hAgo
            : undefined;
        const isPrelaunch =
          !!d.perpetual_info?.pre_launch ||
          (d as any).market_category === "Pre-IPO";
        const label = instrumentLabel(d.asset, InstrumentTypeResponse.Perpetual);
        const row: IPerpetualMarketData = {
          id: `PERPETUAL-${d.asset}`,
          asset: d.asset,
          market: label,
          // Absent mark stays absent ("-"), never a "$0.00" quote.
          price: d.perpetual_info?.mark_price ?? undefined,
          dailyChange,
          dailyVolume: apiNum(d.perpetual_info?.daily_volume),
          isWatchlist: watchlist.some(
            (w) =>
              w.asset === d.asset &&
              w.derivative === InstrumentTypeResponse.Perpetual
          ),
          isPrelaunch,
          category: normalizeMarketCategory(
            (d as any).market_category,
            isPrelaunch
          ),
          isCurrentMarket:
            market.asset === d.asset &&
            market.derivative === InstrumentTypeResponse.Perpetual,
          pricePrecision: getConfigPricePrecision(
            d.asset,
            d.perpetual_info?.amount_step,
            d.perpetual_info?.price_step
          ),
          indexHistory:
            assetIndexHistories?.assets_history?.find(
              (h) => h.asset === d.asset
            )?.history || [],
        };
        return {
          row,
          keys: {
            label,
            asset: d.asset,
            displayName: d.display_name,
            category: d.market_category,
          },
        };
      });

    const spotTagged: Tagged<IPerpetualMarketData>[] = uniqueSpotAssets.map(
      (asset) => {
        const mkt = spotMarketsData?.find((d) => d.underlying_asset === asset);
        const stats = spotStatistics?.[asset];
        const label = instrumentLabel(asset, InstrumentTypeResponse.Spot);
        const price = mkt?.mark_price || mkt?.index_price || undefined;
        const category = mkt?.market_category;
        const row: IPerpetualMarketData = {
          id: `SPOT-${asset}`,
          asset: asset as IPerpetualMarketData["asset"],
          market: label,
          price,
          // From `/statistics`, the source the trading header uses for the
          // same market, so the palette and the header agree. A hardcoded 0
          // was a claim every spot market is flat and traded nothing; with no
          // statistics the cells stay undefined and render "-".
          dailyChange: spotDailyChange(stats, price),
          dailyVolume: apiNum(stats?.daily_volume as any),
          isWatchlist: watchlist.some(
            (w) =>
              w.asset === asset &&
              w.derivative === InstrumentTypeResponse.Spot
          ),
          isPrelaunch: false,
          // Spot markets are grouped like perps, so they show under the
          // "Crypto" chip (their default category) instead of only "All".
          category: normalizeMarketCategory(category, false),
          isCurrentMarket:
            market.asset === asset &&
            market.derivative === InstrumentTypeResponse.Spot,
          pricePrecision: getConfigPricePrecision(
            asset as IPerpetualMarketData["asset"],
            mkt?.amount_step,
            mkt?.price_step
          ),
          indexHistory: [],
        };
        return {
          row,
          keys: { label, asset, displayName: mkt?.display_name, category },
        };
      }
    );

    if (isSearching) {
      rankMarketRows(
        [
          ...(perpsEnabled ? perpTagged : []),
          ...(isInstrumentTypeEnabled(InstrumentTypeResponse.Spot)
            ? spotTagged
            : []),
          ...(isInstrumentTypeEnabled(InstrumentTypeResponse.Option)
            ? optionsTagged
            : []),
        ] as Tagged<IPerpetualMarketData | IOptionMarketData>[],
        searchText,
        (t) => t.keys
      ).forEach((t, index) => rankOf.set(t.row, index));
    }

    const optionsData = finish(optionsTagged);
    const allResults = finish(perpTagged);
    const spotResults = finish(spotTagged);

    const sections: ISectionData[] = [];

    if (
      perpsEnabled &&
      allResults.length &&
      (marketFilter === MarketFiltersEnum.ALL_MARKETS ||
        marketFilter === MarketFiltersEnum.WATCHLIST ||
        perpCategoryFilters.includes(marketFilter as MarketFiltersEnum))
    ) {
      const visibleCategories = perpCategoryFilters.includes(
        marketFilter as MarketFiltersEnum
      )
        ? [marketFilter as MarketFiltersEnum]
        : perpCategoryFilters;

      visibleCategories.forEach((category) => {
        const categoryData = allResults.filter((d) => d.category === category);
        if (!categoryData.length) return;

        sections.push({
          name: t(category),
          instrumentType: InstrumentTypeResponse.Perpetual,
          isPrelaunch: category === MarketFiltersEnum.PRE_IPO,
          data: categoryData,
        });
      });
    }

    if (
      isInstrumentTypeEnabled(InstrumentTypeResponse.Option) &&
      optionsData.length &&
      (marketFilter === MarketFiltersEnum.ALL_MARKETS ||
        marketFilter === MarketFiltersEnum.WATCHLIST)
    ) {
      sections.push({
        name: tTradingType("options"),
        instrumentType: InstrumentTypeResponse.Option,
        isPrelaunch: false,
        data: optionsData,
      });
    }

    // Spot markets section. Under a category chip it lists that category's
    // spot markets -- "Crypto" used to come up empty on a spot-only build.
    const isCategoryFilter = perpCategoryFilters.includes(
      marketFilter as MarketFiltersEnum
    );
    const visibleSpot = isCategoryFilter
      ? spotResults.filter((d) => d.category === marketFilter)
      : spotResults;
    if (
      isInstrumentTypeEnabled(InstrumentTypeResponse.Spot) &&
      visibleSpot.length &&
      (marketFilter === MarketFiltersEnum.ALL_MARKETS ||
        marketFilter === MarketFiltersEnum.WATCHLIST ||
        isCategoryFilter)
    ) {
      sections.push({
        name: tTradingType("perpetual_spot"),
        instrumentType: InstrumentTypeResponse.Spot,
        isPrelaunch: false,
        data: visibleSpot,
      });
    }

    // While searching, the section holding the best match comes first, so the
    // highlighted first row (what Enter opens) is the top-ranked market.
    if (isSearching) {
      const best = (s: ISectionData) =>
        Math.min(...(s.data as unknown[]).map((r) => rankOf.get(r) as number));
      sections.sort((a, b) => best(a) - best(b));
    }

    return sections;
};
